import {useMemo, useCallback} from 'react';

import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {Body} from 'shells/typography/body';

import type {GetAiSearchResults_aiSearch_suggestions as AiSearchSuggestion} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../../../analytics-client';

import {recordVisit} from './frecency';
import {FrecentSection} from './frecent-section';
import {ResultSection} from './result-section';
import {SearchResultAction} from './search-result-action';
import {SearchResultAiSuggestion} from './search-result-ai-suggestion';
import {SearchResultNavigation} from './search-result-navigation';
import {SectionHeader} from './section-header';
import {groupResults, useCommandPaletteSearch} from './use-command-palette-search';

interface Props {
    searchValue: string;
    isLoading: boolean;
    hasTyped: boolean;
    setIsSearchBarFocused: (focused: boolean) => void;
    setSearchValue: (value: string) => void;
    activeIndex: number;
    resultRefs: React.MutableRefObject<(HTMLElement | null)[]>;
    aiSuggestions: AiSearchSuggestion[];
    isAiLoading: boolean;
}

export function CommandPaletteContent(props: Props) {
    const {results, frecentSuggestions} = useCommandPaletteSearch(props.searchValue);

    const grouped = useMemo(() => groupResults(results), [results]);

    const hasSearchResults = results.length > 0;

    const handleResultClick = useCallback(
        (resultId: string) => {
            recordVisit(resultId);
            props.setIsSearchBarFocused(false);
            props.setSearchValue('');
        },
        [props]
    );

    if (props.isLoading && props.hasTyped) {
        return (
            <div className='flex flex-dir-col align-center justify-center flex-grow gap-8 pad-24'>
                <Body size='large' color='light'>
                    Searching…
                </Body>
            </div>
        );
    }

    if (!props.searchValue) {
        if (frecentSuggestions.length === 0) {
            return (
                <NothingFoundEmptyState
                    title='Nothing to see...yet!'
                    subtitle='As soon as you start looking around Nutshell, your recently viewed items will appear.'
                />
            );
        }

        return (
            <FrecentSection
                items={frecentSuggestions}
                activeIndex={props.activeIndex}
                startIndex={0}
                resultRefs={props.resultRefs}
                onClick={handleResultClick}
            />
        );
    }

    if (!hasSearchResults && props.aiSuggestions.length === 0 && !props.isAiLoading) {
        return (
            <NothingFoundEmptyState
                title='No results found'
                subtitle='Try a different search term or browse using the navigation.'
            />
        );
    }

    let refIndex = 0;

    return (
        <div className='full-width flex flex-dir-col overflow-y-auto flex-grow'>
            {grouped.navigation.length > 0 && (
                <ResultSection title='GO TO'>
                    {grouped.navigation.map((result) => {
                        if (result.type !== 'navigation') return null;
                        const index = refIndex++;

                        return (
                            <SearchResultNavigation
                                key={result.item.id}
                                item={result.item}
                                isActive={index === props.activeIndex}
                                innerRef={(el) => {
                                    props.resultRefs.current[index] = el;
                                }}
                                onClick={() => {
                                    analyticsClient.track(
                                        analyticsClient.EVENTS
                                            .TRACK_COMMAND_PALETTE_NAVIGATION_EXECUTED,
                                        {
                                            itemId: result.item.id,
                                            label: result.item.label,
                                            path: result.item.path,
                                            section: result.item.section,
                                            query: props.searchValue,
                                        }
                                    );
                                    handleResultClick(result.item.id);
                                }}
                            />
                        );
                    })}
                </ResultSection>
            )}
            {grouped.actions.length > 0 && (
                <ResultSection title='ACTIONS'>
                    {grouped.actions.map((result) => {
                        if (result.type !== 'action') return null;
                        const idx = refIndex++;

                        return (
                            <SearchResultAction
                                key={result.item.id}
                                item={result.item}
                                isActive={idx === props.activeIndex}
                                innerRef={(el: HTMLDivElement | null) => {
                                    props.resultRefs.current[idx] = el;
                                }}
                                onExecuted={() => handleResultClick(result.item.id)}
                                searchQuery={props.searchValue}
                            />
                        );
                    })}
                </ResultSection>
            )}
            {props.aiSuggestions.length > 0 && (
                <ResultSection title='AI SUGGESTIONS'>
                    {props.aiSuggestions.map((suggestion, i) => {
                        if (!suggestion.path) return null;
                        const idx = refIndex++;

                        return (
                            <SearchResultAiSuggestion
                                key={`ai-${i}`}
                                suggestion={suggestion}
                                isActive={idx === props.activeIndex}
                                innerRef={(el) => {
                                    props.resultRefs.current[idx] = el;
                                }}
                                onClick={() => {
                                    analyticsClient.track(
                                        analyticsClient.EVENTS
                                            .TRACK_COMMAND_PALETTE_AI_SUGGESTION_CLICKED,
                                        {
                                            label: suggestion.label,
                                            path: suggestion.path,
                                            description: suggestion.description,
                                            query: props.searchValue,
                                        }
                                    );
                                    props.setIsSearchBarFocused(false);
                                    props.setSearchValue('');
                                }}
                            />
                        );
                    })}
                </ResultSection>
            )}
            {props.isAiLoading && <SectionHeader>AI is thinking…</SectionHeader>}
        </div>
    );
}
